import "react-app-polyfill/ie11";
import React from "react";
import ReactDOM from "react-dom";
import {
  MDLinxHeader,
  Footer as MDLinxFooter
} from "@mdlinx/component-library";
import styled from "styled-components";
import { createGlobalStyle } from "styled-components";
import { normalize } from "styled-normalize";
import ApolloClient from "apollo-client";
import { createHttpLink } from "apollo-link-http";
import { setContext } from "apollo-link-context";
import gql from "graphql-tag";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloProvider, Query } from "react-apollo";
import { onError } from "apollo-link-error";
import { medicalSpecialties } from "./medical-specialties";
import { persistCache } from "apollo-cache-persist";
import { trackEvent } from "./event-tracking";

const headerMountNode: HTMLElement | null = document.getElementById("header");
const headerFooterBundleConfig: {
  mdlinxUID?: string | number;
  profileName?: string;
  profession?: string;
  spec?: string;
  currentSpec?: string | number;
  siteTitle?: string;
  siteName?: string;
  activeNavTitle?: string;
  zzCode?: string;
  siteEnvironment?: string;
  loggedIn?: string;
} =
  headerMountNode && headerMountNode.dataset.react
    ? JSON.parse(headerMountNode.dataset.react)
    : {};

const footerMountNode: HTMLElement | null = document.getElementById("footer");

const GlobalStyle: React.ComponentClass = createGlobalStyle`
  ${normalize}
  body {  
    overflow-x: hidden;
  }
`;

let loginURL: string = window.location.href;
if (loginURL.indexOf("?") > -1) {
  loginURL += "&poplogin";
} else {
  loginURL += "?poplogin";
}

type SiteNavigationMenuLink = {
  name: string;
  route: string;
  secondary?: SiteNavigationMenuLink[];
};

const headerLinks: {
  home: SiteNavigationMenuLink;
  primary: SiteNavigationMenuLink[];
  profile: {
    messages: SiteNavigationMenuLink;
    links: SiteNavigationMenuLink[];
  };
  mobileMenuFooter: SiteNavigationMenuLink[];
} = {
  home: { name: "home", route: `/` },
  primary: [
    {
      name: "Journal Summaries",
      route: `/${headerFooterBundleConfig.siteName}/journal-summaries/`
    },
    {
      name: "News",
      route: `/${headerFooterBundleConfig.siteName}/top-medical-news/`
    },
    {
      name: "Quizzes",
      route: `/${headerFooterBundleConfig.siteName}/smartestdoc/`,
      secondary: [
        {
          name: "Question of the Day",
          route: `/${headerFooterBundleConfig.siteName}/smartestdoc/`
        },
        {
          name: "Monthly Questions",
          route: `/${headerFooterBundleConfig.siteName}/smartestdoc/`
        },
        {
          name: "Leaderboard",
          route: `/${headerFooterBundleConfig.siteName}/smartestdoc/leaderboard.cfm`
        },
        {
          name: "Archive",
          route: `/${headerFooterBundleConfig.siteName}/smartestdoc/physician-review.cfm`
        },
        {
          name: "B.E.P",
          route: `/${headerFooterBundleConfig.siteName}/smartestdoc/board-exam-prep.cfm`
        }
      ]
    },
    {
      name: "Conferences",
      route: `/${headerFooterBundleConfig.siteName}/conference.cfm`,
      secondary: [
        {
          name: "Find A Conference",
          route: `/${headerFooterBundleConfig.siteName}/conference.cfm`
        },
        {
          name: "Conference Coverage",
          route: `/${headerFooterBundleConfig.siteName}/covered-conferences.cfm`
        },
        {
          name: "Interviews",
          route: `/${headerFooterBundleConfig.siteName}/conference-video-coverage.cfm`
        }
      ]
    },
    {
      name: "Careers",
      route: `/${headerFooterBundleConfig.siteName}/career/`,
      secondary: [
        {
          name: "Jobs",
          route: `/${headerFooterBundleConfig.siteName}/career/`
        },
        {
          name: "Post My CV",
          route: `/${headerFooterBundleConfig.siteName}/career/cv/cv-edit-personal.cfm`
        },
        {
          name: "Search Hospitals",
          route: `/hospital/`
        },
        {
          name: "For Employers",
          route: "http://career.mdlinx.com/recruiter/index.cfm"
        }
      ]
    }
  ],
  profile: {
    messages: {
      name: "My Messages",
      route: `/${headerFooterBundleConfig.siteName}/messages.cfm`
    },
    links: [
      {
        name: "My Profile Settings",
        route: `/${headerFooterBundleConfig.siteName}/edit_profile.cfm`
      },
      {
        name: "My Subscriptions",
        route: `/${headerFooterBundleConfig.siteName}/edit_profile.cfm?edit=ns`
      },
      {
        name: "My Saved Articles",
        route: `/${headerFooterBundleConfig.siteName}/userLibrary.cfm?selectedTab=2`
      }
    ]
  },
  mobileMenuFooter: [
    { name: "About Us", route: `/about-mdlinx.cfm` },
    { name: "Privacy Policy", route: `/privacy-policy/` },
    { name: "Terms of Use", route: `/terms-of-use/` },
    {
      name: "Advertise with MDLinx",
      route: "https://usa.m3.com/integrated-solutions/"
    }
  ]
};

const HeaderContainer = styled.div`
  @import url("https://fonts.googleapis.com/css?family=Work+Sans&display=swap");
  font-family: "Work Sans", sans-serif;
  position: relative;
  top: 0;
  width: 100vw;
  z-index: 1030;
  background-color: white;
  line-height: 1.2;
`;

const handleClick: (link: string) => void = link => {
  window.location.href = link;
};

type Specialty = {
  id: string | number | undefined;
  title: string | undefined;
  siteName: string | undefined;
};

const specObj: Specialty = {
  id: headerFooterBundleConfig.currentSpec,
  title: headerFooterBundleConfig.siteTitle,
  siteName: headerFooterBundleConfig.siteName
};

async function createApolloClient() {
  let apolloURI: string = "";
  if (
    headerFooterBundleConfig.siteEnvironment === "development" ||
    headerFooterBundleConfig.siteEnvironment === "qa"
  ) {
    apolloURI = "https://graphql-gateway.g.dev-c.mdlinx.com/graphql";
  } else {
    apolloURI = "https://graphql-gateway.g.c.mdlinx.com/graphql";
  }

  const httpLink = createHttpLink({
    uri: apolloURI,
    credentials: "include"
  });

  const authLink = setContext((_, { headers }) => {
    // Basic auth is only needed in development
    const headersWithAuth = {
      headers: {
        ...headers,
        Authorization: `Basic bTMtbnljOm0zbTNtM255Yw==`
      }
    };

    const headersWithoutAuth = {
      headers: {
        ...headers
      }
    };

    const requestHeaders =
      headerFooterBundleConfig.siteEnvironment === "development" ||
      headerFooterBundleConfig.siteEnvironment === "qa"
        ? headersWithAuth
        : headersWithoutAuth;

    return {
      ...requestHeaders
    };
  });

  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.map(({ message, locations, path }) =>
        console.log(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
        )
      );
    }
    if (networkError) {
      console.log(`[Network error]: ${networkError}`);
    }
  });

  const cache = new InMemoryCache();

  await persistCache({
    cache,
    storage: window.localStorage
  });

  const client = new ApolloClient({
    link: authLink.concat(httpLink).concat(errorLink),
    cache
  });

  return client;
}

const USER_MESSAGES_QUERY = gql`
  query($queryParams: UserMessageQueryParam!) {
    userMessages(queryParams: $queryParams) {
      id
      companyName
      fromName
      jobTitle
      jobNumber
      picture
      priorityFlag
      repId
      title
    }
  }
`;

type UserMessage = {
  id: string;
  companyName: string;
  jobTitle: string;
  jobNumber: string;
  title: string;
};

interface UserMessagesResponseData {
  userMessages: UserMessage[];
}

interface UserMessageQueryVariables {
  queryParams: {
    zzCode?: string;
  };
}

class Header extends React.Component {
  state = {
    client: null,
    loaded: false
  };

  async componentDidMount() {
    const client = await createApolloClient();
    this.setState({
      client,
      loaded: true
    });
    if (
      window.location.href.includes("/messages.cfm") ||
      window.location.href.includes("/message_detail.cfm")
    ) {
      this.updateCache();
    }
    if (window.location.href.includes("logout")) {
      window.localStorage.removeItem("apollo-cache-persist");
      client.clearStore();
    }
  }

  updateCache = () => {
    this.state.client.query({
      query: USER_MESSAGES_QUERY,
      variables: {
        queryParams: {
          zzCode: headerFooterBundleConfig.zzCode
        }
      },
      fetchPolicy: "network-only"
    });
  };

  onLinkClicked: (link: SiteNavigationMenuLink) => void = link => {
    if (link.name === "SignOut") {
      window.localStorage.removeItem("apollo-cache-persist");
      this.state.client.clearStore();
      handleClick(`/${headerFooterBundleConfig.siteName}/logout.cfm`);
    } else if (link.name === "SignUp") {
      handleClick(`/register/`);
    } else if (link.name === "login") {
      handleClick(loginURL);
    } else if (link.name === "message") {
      this.updateCache();
      trackEvent("click_event", {
        ca: "message-click",
        url: `/${headerFooterBundleConfig.siteName}/${link.route}`,
        uid: headerFooterBundleConfig.mdlinxUID
      });
      handleClick(`/${headerFooterBundleConfig.siteName}/${link.route}`);
    } else if (link.name === "My Messages") {
      this.updateCache();
      trackEvent("click_event", {
        ca: "my-messages-click",
        url: link.route,
        uid: headerFooterBundleConfig.mdlinxUID
      });
      handleClick(link.route);
    } else if (link.name === "user-profile-menu") {
      this.updateCache();
      trackEvent("click_event", {
        ca: "user-profile-menu-click",
        uid: headerFooterBundleConfig.mdlinxUID
      });
    } else {
      handleClick(link.route);
    }
  };

  render() {
    const { client, loaded } = this.state;

    return (
      <HeaderComponent userMessages={[]} onLinkClicked={this.onLinkClicked} />
    );

    /*
    if (!loaded) {
      return (
        <HeaderComponent userMessages={[]} onLinkClicked={this.onLinkClicked} />
      );
    }

    return (
      <ApolloProvider client={client}>
        <Query<UserMessagesResponseData, UserMessageQueryVariables>
          query={USER_MESSAGES_QUERY}
          variables={{
            queryParams: {
              zzCode: headerFooterBundleConfig.zzCode
            }
          }}
          skip={!headerFooterBundleConfig.zzCode}
        >
          {userMessagesResponse => {
            if (userMessagesResponse.error) {
              console.error(userMessagesResponse.error);
            }

            const userMessages =
              userMessagesResponse.loading ||
              // if there is an error will display no message
              userMessagesResponse.error ||
              !userMessagesResponse.data
                ? []
                : userMessagesResponse.data.userMessages;

            return (
              <HeaderComponent
                userMessages={userMessages}
                onLinkClicked={this.onLinkClicked}
              />
            );
          }}
        </Query>
      </ApolloProvider>
    );
    */
  }
}

interface HeaderComponentProps {
  userMessages: UserMessage[];
  onLinkClicked: (link: SiteNavigationMenuLink) => void;
}

const HeaderComponent = ({
  userMessages,
  onLinkClicked
}: HeaderComponentProps) => {
  const onSelectSpecialty: (specialty: Specialty) => void = specialty => {
    handleClick(`/${specialty.siteName}/`);
  };

  return (
    <>
      <GlobalStyle />
      <HeaderContainer>
        <MDLinxHeader
          navLinks={headerLinks}
          specialties={medicalSpecialties}
          currentSpecialty={specObj}
          onSelectSpecialty={onSelectSpecialty}
          onLinkClicked={onLinkClicked}
          isLoggedIn={
            headerFooterBundleConfig.loggedIn
              ? JSON.parse(headerFooterBundleConfig.loggedIn)
              : false
          }
          profileName={
            headerFooterBundleConfig.profileName
              ? headerFooterBundleConfig.profileName
              : ""
          }
          activePathName={headerFooterBundleConfig.activeNavTitle}
          userMessages={userMessages}
          numberUnreadMessages={userMessages.length}
        />
      </HeaderContainer>
    </>
  );
};

const footerLinks: {
  desktop: {
    set1: SiteNavigationMenuLink[];
    set2: SiteNavigationMenuLink[];
  };
  mobile: SiteNavigationMenuLink[];
} = {
  desktop: {
    set1: [
      { name: "About Us", route: `/about-mdlinx.cfm` },
      { name: "Our Products", route: `/products-and-services/` },
      { name: "Site Map", route: `/sitemap.cfm` },
      {
        name: "FAQs",
        route: `/${headerFooterBundleConfig.siteName}/help.cfm`
      },
      {
        name: "Advertise with MDLinx",
        route: "https://usa.m3.com/integrated-solutions/"
      }
    ],
    set2: [
      { name: "Contact Us", route: "https://usa.m3.com/contact-us/" },
      { name: "Privacy Policy", route: `/privacy-policy/` },
      { name: "Terms of Use", route: `/terms-of-use/` }
    ]
  },
  mobile: [
    { name: "About Us", route: `/about-mdlinx.cfm` },
    { name: "Privacy Policy", route: `/privacy-policy/` },
    { name: "Terms of Use", route: `/terms-of-use/` },
    {
      name: "Advertise with MDLinx",
      route: "https://usa.m3.com/integrated-solutions/"
    }
  ]
};

const Footer = () => {
  return (
    <>
      <MDLinxFooter
        footerLinks={footerLinks}
        onLinkClicked={link => handleClick(link.route)}
      />
    </>
  );
};

ReactDOM.render(<Header />, headerMountNode);
ReactDOM.render(<Footer />, footerMountNode);
